import React, { useEffect, useState, useRef } from "react";
import {
  Search,
  MoreVertical,
  Send,
  Paperclip,
  Smile,
  Users,
  Clock,
  Star,
  ChevronLeft,
  MessageCircle,
  Info
} from "lucide-react";
import configs from "../config";
import { fetchWithTenant } from "../utils/apiClient";
import useAuthStore from '../store/authStore';
import { usePermissions } from '../hooks/usePermissions';
import { useDocumentTitle } from '../hooks/useDocumentTitle';
import { useNavigate } from "react-router-dom";

const READ_TS_STORAGE_KEY = 'message-read-timestamps';

const getRoomLatestTimestamp = (room) => {
  const messages = room?.messages || [];
  if (messages.length === 0) {
    return new Date(room?.createdDateTime || 0).getTime();
  }

  let latest = 0;
  for (const message of messages) {
    const timestamp = message?.timestamp ? new Date(message.timestamp).getTime() : 0;
    if (timestamp > latest) latest = timestamp;
  }

  return latest || new Date(room?.createdDateTime || 0).getTime();
};

export function MessageBoardPage() {
  const { user, isAuthenticated, hasHydrated } = useAuthStore();
  const { canReadMessageBoard } = usePermissions();
  const navigate = useNavigate();
  const messagesEndRef = useRef(null);

  const [rooms, setRooms] = useState([]);
  const [selectedRoom, setSelectedRoom] = useState(null);
  const [upworkUserId, setUpworkUserId] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [newMessage, setNewMessage] = useState("");
  const [showInfo, setShowInfo] = useState(false);
  const [readTimestamps, setReadTimestamps] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem(READ_TS_STORAGE_KEY) || '{}');
    } catch { return {}; }
  });

  useDocumentTitle('Messages - Propel');

  useEffect(() => {
    if (hasHydrated && (!isAuthenticated || !canReadMessageBoard())) {
      navigate("/", { replace: true });
    }
  }, [isAuthenticated, hasHydrated, canReadMessageBoard, navigate]);

  // Fetch rooms
  useEffect(() => {
    const fetchRooms = async () => {
      try {
        const res = await fetchWithTenant(`${configs.BACKEND_URL}/api/room/getAll`, {
          method: "GET",
        });

        if (!res.ok) throw new Error("Failed to fetch rooms");

        const data = await res.json();
        const roomList = data.rooms || data;
        setRooms(roomList);
        setUpworkUserId(data.upworkUserId || null);

        // Treat historical messages as read by default on first load.
        setReadTimestamps((previous) => {
          const seeded = { ...previous };
          let changed = false;

          for (const room of roomList) {
            if (!room?.roomId) continue;
            if (seeded[room.roomId] != null) continue;
            seeded[room.roomId] = getRoomLatestTimestamp(room) || Date.now();
            changed = true;
          }

          if (changed) {
            localStorage.setItem(READ_TS_STORAGE_KEY, JSON.stringify(seeded));
            return seeded;
          }

          return previous;
        });

        // Auto-select first room and mark as read
        if (roomList.length > 0 && !selectedRoom) {
          setSelectedRoom(roomList[0]);
          markRoomAsRead(roomList[0].roomId);
        }
      } catch (error) {
        setError(error.message);
      } finally {
        setIsLoading(false);
      }
    };

    if (isAuthenticated) {
      fetchRooms();
    }
  }, [isAuthenticated]);

  // Auto-scroll to bottom when room changes or messages update
  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [selectedRoom]);

  // Format relative time
  const getRelativeTime = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / (1000 * 60));
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffDays = Math.floor(diffHours / 24);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m`;
    if (diffHours < 24) return `${diffHours}h`;
    if (diffDays < 7) return `${diffDays}d`;
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  // Format full date for message timestamps
  const formatDate = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    return date.toLocaleTimeString("en-US", {
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  // Format date divider
  const formatDateDivider = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);

    if (date.toDateString() === today.toDateString()) return "Today";
    if (date.toDateString() === yesterday.toDateString()) return "Yesterday";

    const opts = { weekday: "long", month: "short", day: "numeric" };
    if (date.getFullYear() !== new Date().getFullYear()) opts.year = "numeric";
    return date.toLocaleDateString("en-US", opts);
  };

  // Check if message is from the current user
  const isOwnMessage = (message) => {
    if (!upworkUserId) return false;
    return message.userId === upworkUserId;
  };

  // Mark a room as read by storing current timestamp
  const markRoomAsRead = (roomId) => {
    setReadTimestamps((previous) => {
      const updated = { ...previous, [roomId]: Date.now() };
      localStorage.setItem(READ_TS_STORAGE_KEY, JSON.stringify(updated));
      return updated;
    });
  };

  // Check if a room has unread messages
  const isRoomUnread = (room) => {
    const lastRead = readTimestamps[room.roomId] || 0;
    const latestTime = getRoomLatestTime(room);
    return latestTime > lastRead;
  };

  // Get the latest timestamp from a room (last message or room creation)
  const getRoomLatestTime = (room) => {
    return getRoomLatestTimestamp(room);
  };

  // Filter rooms by search and sort by latest activity
  const filteredRooms = rooms
    .filter(room =>
      (room.roomName || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
      (room.topic || '').toLowerCase().includes(searchQuery.toLowerCase())
    )
    .sort((a, b) => getRoomLatestTime(b) - getRoomLatestTime(a));

  // Get last message info for room list
  const getLastMessage = (room) => {
    const messages = room.messages || [];
    if (messages.length === 0) return { text: 'No messages yet', time: null };
    const sorted = [...messages].sort((a, b) => {
      const timeA = a.timestamp ? new Date(a.timestamp).getTime() : 0;
      const timeB = b.timestamp ? new Date(b.timestamp).getTime() : 0;
      return timeB - timeA;
    });
    const lastMsg = sorted[0];
    const isOwn = isOwnMessage(lastMsg);
    const prefix = isOwn ? 'You: ' : '';
    const text = lastMsg?.message
      ? prefix + lastMsg.message.slice(0, 50) + (lastMsg.message.length > 50 ? '...' : '')
      : 'No messages';
    return { text, time: lastMsg?.timestamp };
  };

  // Sort messages by timestamp with messageId as tiebreaker
  const getSortedMessages = (room) => {
    if (!room?.messages) return [];
    return [...room.messages].sort((a, b) => {
      const timeA = a.timestamp ? new Date(a.timestamp).getTime() : 0;
      const timeB = b.timestamp ? new Date(b.timestamp).getTime() : 0;
      if (timeA !== timeB) return timeA - timeB;
      return (a.messageId || '').localeCompare(b.messageId || '');
    });
  };

  // Check if a date divider should be shown before this message
  const shouldShowDateDivider = (messages, index) => {
    if (index === 0) return true;
    const curr = messages[index].timestamp;
    const prev = messages[index - 1].timestamp;
    if (!curr || !prev) return false;
    return new Date(curr).toDateString() !== new Date(prev).toDateString();
  };

  // Check if this message starts a new sender group
  const isFirstInGroup = (messages, index) => {
    if (index === 0) return true;
    const curr = messages[index];
    const prev = messages[index - 1];
    // New group if different sender or date divider between them
    if (curr.userId !== prev.userId) return true;
    if (shouldShowDateDivider(messages, index)) return true;
    return false;
  };

  // Get sender display name
  const getSenderName = (message, room) => {
    if (isOwnMessage(message)) return 'You';
    return message.userName || room.ownerName || 'Participant';
  };

  // Handle send message (placeholder)
  const handleSendMessage = (e) => {
    e.preventDefault();
    if (!newMessage.trim()) return;
    // TODO: Implement send message API
    setNewMessage("");
  };

  if (!isAuthenticated) {
    return null;
  }

  return (
    <div className="h-[calc(100vh-4rem)] bg-slate-50 flex">
      {/* Left Panel - Conversation List */}
      <div className="w-80 bg-white border-r border-gray-200 flex flex-col flex-shrink-0">
        {/* Search Header */}
        <div className="p-4 border-b border-gray-200">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search messages..."
              className="w-full pl-9 pr-4 py-2 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors"
            />
          </div>
        </div>

        {/* Room List */}
        <div className="flex-1 overflow-y-auto">
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-500"></div>
            </div>
          ) : error ? (
            <div className="p-4 text-center text-red-600 text-sm">
              {error}
            </div>
          ) : filteredRooms.length === 0 ? (
            <div className="p-4 text-center text-gray-500 text-sm">
              {searchQuery ? 'No conversations found' : 'No messages yet'}
            </div>
          ) : (
            filteredRooms.map((room) => {
              const lastMessage = getLastMessage(room);
              const unread = isRoomUnread(room) && selectedRoom?.roomId !== room.roomId;
              return (
                <div
                  key={room.roomId}
                  onClick={() => { setSelectedRoom(room); markRoomAsRead(room.roomId); }}
                  className={`p-4 border-b border-gray-100 cursor-pointer transition-colors hover:bg-gray-50 ${
                    selectedRoom?.roomId === room.roomId ? 'bg-primary-50 border-l-2 border-l-primary-600' : ''
                  }`}
                >
                  <div className="flex items-start gap-3">
                    {/* Unread Indicator */}
                    {unread && (
                      <div className="w-2.5 h-2.5 rounded-full bg-primary-500 flex-shrink-0 mt-4" />
                    )}

                    {/* Avatar */}
                    <div className="w-10 h-10 rounded-full bg-primary-100 flex items-center justify-center flex-shrink-0">
                      <span className="text-primary-700 font-medium text-sm">
                        {(room.roomName || 'R')[0].toUpperCase()}
                      </span>
                    </div>

                    {/* Content */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between mb-0.5">
                        <h3 className={`text-sm truncate ${unread ? 'font-semibold text-gray-900' : 'font-medium text-gray-900'}`}>
                          {room.roomName || 'Untitled Room'}
                        </h3>
                        <span className="text-xs text-gray-500 flex-shrink-0 ml-2">
                          {getRelativeTime(lastMessage.time || room.createdDateTime)}
                        </span>
                      </div>
                      {room.topic && (
                        <p className="text-xs text-gray-400 truncate mb-0.5">
                          {room.topic}
                        </p>
                      )}
                      <p className="text-xs text-gray-500 truncate">
                        {lastMessage.text}
                      </p>
                      <div className="flex items-center gap-2 mt-1">
                        <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-600">
                          {room.roomType || 'General'}
                        </span>
                        {room.messages?.length > 0 && (
                          <span className="text-xs text-gray-400 flex items-center gap-1">
                            <MessageCircle className="w-3 h-3" />
                            {room.messages.length}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>

      {/* Center Panel - Chat Area */}
      <div className="flex-1 flex flex-col bg-white">
        {selectedRoom ? (
          <>
            {/* Chat Header */}
            <div className="h-16 px-4 flex items-center justify-between border-b border-gray-200 flex-shrink-0">
              <div className="flex items-center gap-3">
                <button
                  onClick={() => setSelectedRoom(null)}
                  className="lg:hidden p-2 text-gray-600 hover:bg-gray-100 rounded-lg"
                >
                  <ChevronLeft className="w-5 h-5" />
                </button>
                <div className="w-10 h-10 rounded-full bg-primary-100 flex items-center justify-center">
                  <span className="text-primary-700 font-medium">
                    {(selectedRoom.roomName || 'R')[0].toUpperCase()}
                  </span>
                </div>
                <div>
                  <h2 className="font-semibold text-gray-900">
                    {selectedRoom.roomName || 'Untitled Room'}
                  </h2>
                  <p className="text-xs text-gray-500">
                    {selectedRoom.topic || `${selectedRoom.numberOfUsers || 0} participants`}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <button
                  className="p-2 text-gray-600 hover:bg-gray-100 rounded-lg"
                  title="Favorite"
                >
                  <Star className="w-5 h-5" />
                </button>
                <button
                  onClick={() => setShowInfo(!showInfo)}
                  className={`p-2 rounded-lg transition-colors ${
                    showInfo ? 'bg-primary-100 text-primary-700' : 'text-gray-600 hover:bg-gray-100'
                  }`}
                  title="Room Info"
                >
                  <Info className="w-5 h-5" />
                </button>
                <button
                  className="p-2 text-gray-600 hover:bg-gray-100 rounded-lg"
                  title="More options"
                >
                  <MoreVertical className="w-5 h-5" />
                </button>
              </div>
            </div>

            {/* Messages Area */}
            <div className="flex-1 overflow-y-auto p-4 bg-slate-50">
              {(() => {
                const sortedMessages = getSortedMessages(selectedRoom);
                if (sortedMessages.length === 0) {
                  return (
                    <div className="flex flex-col items-center justify-center h-full text-gray-500">
                      <MessageCircle className="w-12 h-12 mb-2 text-gray-300" />
                      <p className="text-sm">No messages yet</p>
                      <p className="text-xs text-gray-400">Start the conversation!</p>
                    </div>
                  );
                }

                return (
                  <div className="space-y-1">
                    {sortedMessages.map((message, index) => {
                      const isOwn = isOwnMessage(message);
                      const showDivider = shouldShowDateDivider(sortedMessages, index);
                      const firstInGroup = isFirstInGroup(sortedMessages, index);
                      const senderName = getSenderName(message, selectedRoom);

                      return (
                        <React.Fragment key={message.messageId || index}>
                          {/* Date Divider */}
                          {showDivider && (
                            <div className="flex items-center gap-3 my-4">
                              <div className="flex-1 h-px bg-gray-200" />
                              <span className="text-xs text-gray-500 font-medium px-2">
                                {formatDateDivider(message.timestamp)}
                              </span>
                              <div className="flex-1 h-px bg-gray-200" />
                            </div>
                          )}

                          {/* Message */}
                          <div className={`flex ${isOwn ? 'justify-end' : 'justify-start'} ${firstInGroup ? 'mt-3' : 'mt-0.5'}`}>
                            {/* Avatar for other's messages */}
                            {!isOwn && (
                              <div className="w-8 flex-shrink-0 mr-2">
                                {firstInGroup ? (
                                  <div className="w-8 h-8 rounded-full bg-primary-100 flex items-center justify-center">
                                    <span className="text-primary-700 font-medium text-xs">
                                      {(senderName || 'P')[0].toUpperCase()}
                                    </span>
                                  </div>
                                ) : (
                                  <div className="w-8" />
                                )}
                              </div>
                            )}

                            <div className={`max-w-[65%]`}>
                              {/* Sender name + time for first message in group */}
                              {firstInGroup && !isOwn && (
                                <div className="flex items-baseline gap-2 mb-1 ml-1">
                                  <span className="text-xs font-medium text-gray-700">
                                    {senderName}
                                  </span>
                                  <span className="text-xs text-gray-400">
                                    {formatDate(message.timestamp)}
                                  </span>
                                </div>
                              )}

                              {/* Message bubble */}
                              <div
                                className={`px-3.5 py-2 ${
                                  isOwn
                                    ? 'bg-primary-600 text-white rounded-2xl rounded-br-md'
                                    : 'bg-white text-gray-900 rounded-2xl rounded-bl-md shadow-sm border border-gray-100'
                                }`}
                              >
                                <p className="text-sm whitespace-pre-wrap break-words">{message.message}</p>
                              </div>

                              {/* Time for own messages (first in group) or non-first messages */}
                              {isOwn && firstInGroup && (
                                <p className="text-xs text-gray-400 mt-1 text-right mr-1">
                                  {formatDate(message.timestamp)}
                                </p>
                              )}
                            </div>
                          </div>
                        </React.Fragment>
                      );
                    })}
                    <div ref={messagesEndRef} />
                  </div>
                );
              })()}
            </div>

            {/* Message Input */}
            <div className="p-4 border-t border-gray-200 bg-white">
              <form onSubmit={handleSendMessage} className="flex items-end gap-2">
                <div className="flex-1 relative">
                  <textarea
                    value={newMessage}
                    onChange={(e) => setNewMessage(e.target.value)}
                    placeholder="Type a message..."
                    rows={1}
                    className="w-full px-4 py-2 pr-20 text-sm border border-gray-300 rounded-md resize-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors"
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && !e.shiftKey) {
                        e.preventDefault();
                        handleSendMessage(e);
                      }
                    }}
                  />
                  <div className="absolute right-2 bottom-2 flex items-center gap-1">
                    <button
                      type="button"
                      className="p-1.5 text-gray-400 hover:text-gray-600 rounded"
                    >
                      <Paperclip className="w-4 h-4" />
                    </button>
                    <button
                      type="button"
                      className="p-1.5 text-gray-400 hover:text-gray-600 rounded"
                    >
                      <Smile className="w-4 h-4" />
                    </button>
                  </div>
                </div>
                <button
                  type="submit"
                  disabled={!newMessage.trim()}
                  className="p-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  <Send className="w-5 h-5" />
                </button>
              </form>
            </div>
          </>
        ) : (
          <div className="flex-1 flex flex-col items-center justify-center text-gray-500 bg-slate-50">
            <MessageCircle className="w-16 h-16 mb-4 text-gray-300" />
            <h3 className="text-lg font-medium text-gray-900 mb-1">Your Messages</h3>
            <p className="text-sm">Select a conversation to start chatting</p>
          </div>
        )}
      </div>

      {/* Right Panel - Room Info (Conditional) */}
      {selectedRoom && showInfo && (
        <div className="w-80 bg-white border-l border-gray-200 flex-shrink-0 overflow-y-auto hidden lg:block">
          <div className="p-4 border-b border-gray-200">
            <h3 className="font-semibold text-gray-900">Room Details</h3>
          </div>

          <div className="p-4 space-y-4">
            {/* Room Info */}
            <div className="space-y-3">
              <div className="flex items-center gap-3">
                <div className="w-16 h-16 rounded-full bg-primary-100 flex items-center justify-center">
                  <span className="text-primary-700 font-bold text-xl">
                    {(selectedRoom.roomName || 'R')[0].toUpperCase()}
                  </span>
                </div>
                <div>
                  <h4 className="font-medium text-gray-900">{selectedRoom.roomName}</h4>
                  <p className="text-sm text-gray-500">{selectedRoom.roomType || 'General'}</p>
                </div>
              </div>

              {selectedRoom.topic && (
                <div className="bg-gray-50 p-3 rounded-lg">
                  <p className="text-xs text-gray-500 mb-1">Topic</p>
                  <p className="text-sm text-gray-900">{selectedRoom.topic}</p>
                </div>
              )}
            </div>

            {/* Stats */}
            <div className="border-t border-gray-100 pt-4 space-y-3">
              <div className="flex items-center justify-between text-sm">
                <span className="text-gray-500 flex items-center gap-2">
                  <Users className="w-4 h-4" />
                  Participants
                </span>
                <span className="font-medium text-gray-900">{selectedRoom.numberOfUsers || 0}</span>
              </div>
              <div className="flex items-center justify-between text-sm">
                <span className="text-gray-500 flex items-center gap-2">
                  <MessageCircle className="w-4 h-4" />
                  Messages
                </span>
                <span className="font-medium text-gray-900">{selectedRoom.messages?.length || 0}</span>
              </div>
              <div className="flex items-center justify-between text-sm">
                <span className="text-gray-500 flex items-center gap-2">
                  <Clock className="w-4 h-4" />
                  Created
                </span>
                <span className="font-medium text-gray-900">
                  {selectedRoom.createdDateTime
                    ? new Date(selectedRoom.createdDateTime).toLocaleDateString("en-US", {
                        month: "short",
                        day: "numeric",
                        year: "numeric",
                      })
                    : ""}
                </span>
              </div>
            </div>

            {/* Owner */}
            {selectedRoom.ownerName && (
              <div className="border-t border-gray-100 pt-4">
                <p className="text-xs text-gray-500 mb-2">Room Owner</p>
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 rounded-full bg-secondary-100 flex items-center justify-center">
                    <span className="text-secondary-700 font-medium text-xs">
                      {selectedRoom.ownerName[0].toUpperCase()}
                    </span>
                  </div>
                  <span className="text-sm font-medium text-gray-900">{selectedRoom.ownerName}</span>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
